import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getAppParams, getUrlParams } from "../apiService/timespam";
import { APIV4_BASE_URL, API_BASE_URL } from "../apiService/apiClient";
import "./Theme.css";
import "./HealthLocker.css";

/* ---- SVG Icons ---- */
const IconFileDoc = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <polyline points="10 9 9 9 8 9"/>
  </svg>
);

/* ---- Parse aiReportJson safely ---- */
const parseAiReport = (str) => {
  if (!str) return null;
  try {
    const parsed = JSON.parse(str);
    return parsed && typeof parsed === "object" && Object.keys(parsed).length > 0 ? parsed : null;
  } catch {
    return null;
  }
};

const HealthLocker = () => {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [reportData, setReportData] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [view, setView] = useState("list"); // list | aiReport
  const [currentAiReport, setCurrentAiReport] = useState(null);
  // const [loadingAttachmentId, setLoadingAttachmentId] = useState(null);
  const menuRef = useRef(null);

  const { theme, lang } = getUrlParams();
  const { goqiiUserId, nonce, signature, apiKey, goqiiAccessToken, appVersion, appType } = getAppParams();
  const pagination = new URLSearchParams(window.location.search).get("pagination") || "";

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    if (lang === "ar") document.documentElement.dir = "rtl";
    else document.documentElement.dir = "ltr";
  }, [theme, lang]);

  const buildQuery = (extra = {}) =>
    new URLSearchParams({ goqiiUserId, nonce, signature, apiKey, goqiiAccessToken, pagination, appVersion, appType, ...extra }).toString();

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/goqiiapihealthlocker/fetch_health_records_resources`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "lang": lang },
        body: JSON.stringify({ goqiiUserId, nonce, signature, apiKey, goqiiAccessToken, pagination, appVersion, appType }),
      });
      const json = await res.json();
      if (res.ok && json.code === 200) {
        setReportData(json.data.healthRecords || []);
        setDocumentCategories(json.data.documentCategories || []);
      } else {
        throw new Error(json.message || t("healthLocker.error"));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRecords(); }, []);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenuIndex(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filterByTab = () => {
    if (tabValue === 0) return reportData;
    const cat = documentCategories[tabValue - 1];
    return reportData.filter((r) => r.documentType === cat?.documentType);
  };

  const deleteRecord = async (healthRecordId) => {
    setOpenMenuIndex(null);
    try {
      const res = await fetch(`${APIV4_BASE_URL}/user/remove_health_record?${buildQuery({ healthRecordId })}`);
      const json = await res.json();
      if (res.ok && json.code === 200) await fetchRecords();
      else throw new Error(json.message || t("healthLocker.deleteFailed"));
    } catch (err) { setError(err.message); }
  };

  const openSmartReport = () => {
    const data = JSON.stringify({ fsn: "223", fssn: "0", fai: { url: "https://tracker-insight.goqii.com/smart_report?isReportOnly=N", title: t("healthLocker.reportDetails") } });
    window.AndroidInterface?.openAppScreenViaNavigation?.(data);
    window.webkit?.messageHandlers?.openAppScreenViaNavigation?.postMessage?.(data);
  };

  const handleViewAiReport = (aiReport, attachments, documentName) => {
    setCurrentAiReport({ report: aiReport, attachments, documentName });
    setView("aiReport");
  };

  const openFileLink = (fileLink) => {
    if (!fileLink) return;
    const data = JSON.stringify({ action: "openFile", url: fileLink });
    // Native bridge — app handles file opening (PDF viewer, image viewer, etc.)
    window.AndroidInterface?.openFile?.(data);
    window.webkit?.messageHandlers?.openFile?.postMessage?.(data);
    // Fallback for browser / WebView without bridge: same-tab navigation works in all WebViews
    window.location.href = fileLink;
  };

  // const handleOriginalClick = async (attachmentId) => {
  //   if (!attachmentId || loadingAttachmentId) return;
  //   try {
  //     setLoadingAttachmentId(attachmentId);
  //     const res = await fetch(`https://goqii2api.goqii.com/goqiiapihealthlocker/fetch_health_record_file_link`, {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json", "lang": lang },
  //       body: JSON.stringify({ goqiiCoachId: "0", attachmentId }),
  //     });
  //     const json = await res.json();
  //     if (json.code === 200 && json.data?.fileLink) {
  //       openFileLink(json.data.fileLink);
  //     }
  //   } catch (e) {
  //     console.error(e);
  //   } finally {
  //     setLoadingAttachmentId(null);
  //   }
  // };

  const handleOriginalClick = (record) => {
    const payload = JSON.stringify(record);
    window.AndroidInterface?.viewOriginal?.(payload);
    window.webkit?.messageHandlers?.viewOriginal?.postMessage?.(payload);
  };

  const openAddRecord = () => {
    const data = JSON.stringify({ fsn: "6", fssn: "0", fai: { title: t("healthLocker.addRecord") } });
    window.AndroidInterface?.openAppScreenViaNavigation?.(data);
    window.webkit?.messageHandlers?.openAppScreenViaNavigation?.postMessage?.(data);
  };

  // ---- Loading ----
  if (loading) {
    return (
      <div className="hl-wrapper">
        <div className="hl-center">
          <img
            src={theme === "dark" ? "https://appcdn.goqii.com/storeimg/44438_1778481784.png" : "https://appcdn.goqii.com/storeimg/11129_1725520285.jpg"}
            alt="GOQii" className="hl-loading-logo"
          />
          <div className="hl-progress-wrap"><div className="hl-progress-bar" /></div>
          <p className="hl-loading-text">{t("healthLocker.loading")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="hl-wrapper">
        <div className="hl-center"><p className="hl-error-text">{error}</p></div>
      </div>
    );
  }

  /* ---- AI Report Detail View ---- */
  if (view === "aiReport" && currentAiReport) {
    const { report, attachments } = currentAiReport;
    const keyCount = report.keyValues?.length || 0;
    const att = attachments?.[0];

    return (
      <div className="hl-wrapper">
        <div className="hl-content">
          {/* AI hero banner */}
          <div className="hl-air-hero-banner">
            <span className="hl-air-hero-star">★</span>
            <p className="hl-air-hero-text">{t("healthLocker.aiExtracted", { count: keyCount })}</p>
          </div>

          {/* File preview card */}
          {att && (
            <div className="hl-air-file-card">
              <span className="hl-air-file-icon-wrap"><IconFileDoc /></span>
              <div className="hl-air-file-info">
                <p className="hl-air-file-name">{att.fileName}</p>
              </div>
              <button
                className="hl-air-view-btn"
                onClick={() => handleOriginalClick(currentAiReport)}
              >
                {t("healthLocker.view")}
              </button>
            </div>
          )}

          {/* Record Name */}
          <div className="hl-air-field-group">
            <p className="hl-air-field-label">
              {t("healthLocker.recordName")} <span className="hl-air-ai-badge">★ AI</span>
            </p>
            <div className="hl-air-field-box">{report.recordName}</div>
          </div>

          {/* Record Type */}
          <div className="hl-air-field-group">
            <p className="hl-air-field-label">
              {t("healthLocker.recordType")} <span className="hl-air-ai-badge">★ AI</span>
            </p>
            <div className="hl-air-field-box">🧪 {report.recordType}</div>
          </div>

          {/* Related To */}
          <div className="hl-air-field-group">
            <p className="hl-air-field-label">
              {t("healthLocker.relatedTo")} <span className="hl-air-ai-badge">★ AI</span>
            </p>
            <div className="hl-air-field-box">🩸 {report.recordRelatedTo}</div>
          </div>

          {/* Date of Record */}
          <div className="hl-air-field-group">
            <p className="hl-air-field-label">
              {t("healthLocker.dateOfRecord")} <span className="hl-air-ai-badge">★ AI</span>
            </p>
            <div className="hl-air-field-box">📅 {report.dateOfRecord}</div>
          </div>

          {/* Key Values Detected */}
          {report.keyValues?.length > 0 && (
            <div className="hl-air-kv-section">
              <p className="hl-air-kv-title">★ {t("healthLocker.keyValuesDetected")}</p>
              {report.keyValues.map((kv, i) => (
                <div key={i} className="hl-air-kv-row">
                  <span className="hl-air-kv-name">{kv.parameterName}</span>
                  <span className="hl-air-kv-val">
                    {kv.value} <span className="hl-air-kv-unit">{kv.unit}</span>
                  </span>
                  <span
                    className="hl-air-kv-status"
                    style={{ color: kv.statusColor, background: `${kv.statusColor}1a` }}
                  >
                    {kv.status}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Notes */}
          <div className="hl-air-field-group">
            <div className="hl-air-notes-header">
              <p className="hl-air-field-label">{t("healthLocker.notes")}</p>
              <span className="hl-air-optional-tag">{t("healthLocker.optional")}</span>
            </div>
            <div className="hl-air-field-box hl-air-notes-box">
              {report.noteAboutReport
                ? <span>{report.noteAboutReport}</span>
                : <span className="hl-air-notes-ph">{t("healthLocker.notesPlaceholder")}</span>
              }
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ---- List View ---- */
  const visibleReports = filterByTab();
  const showTrendBtn = tabValue === 1;

  return (
    <div className="hl-wrapper">
      {/* Tabs */}
      <div className="hl-tabs-bar">
        <button className={`hl-tab${tabValue === 0 ? " active" : ""}`} onClick={() => setTabValue(0)}>
          {t("healthLocker.allRecords")}
        </button>
        {documentCategories.map((cat, i) => (
          <button key={i} className={`hl-tab${tabValue === i + 1 ? " active" : ""}`} onClick={() => setTabValue(i + 1)}>
            {cat.documentTitle}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="hl-content">
        {showTrendBtn && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button className="hl-trend-btn" onClick={openSmartReport}>
              📊 {t("healthLocker.trendReport")}
            </button>
          </div>
        )}

        {visibleReports.length === 0 ? (
          <div className="hl-empty">{t("healthLocker.noRecords")}</div>
        ) : (
          <div className="hl-grid">
            {visibleReports.map((report, idx) => {
              const aiReport = parseAiReport(report.aiReportJson);
              const hasAiReport = !!aiReport;
              const att = report.attachments?.[0];

              return (
                <div className="hl-card" key={idx}>
                  <div className="hl-card-body">
                    <p className="hl-card-title">{report.documentName}</p>
                    <p className="hl-card-date">
                      {t("healthLocker.uploadedOn")} {new Date(report.createdTime).toLocaleDateString()}
                    </p>
                    <div className="hl-divider" />

                    {!hasAiReport && (
                      <p className="hl-report-info">{t("healthLocker.noAiReport")}</p>
                    )}
                  </div>

                  <div className="hl-card-actions">
                    {hasAiReport && (
                      <button
                        className="hl-btn-outline hl-btn-flex"
                        onClick={() => handleViewAiReport(aiReport, report.attachments, report.documentName)}
                      >
                        ✦ {t("healthLocker.aiReport")}
                      </button>
                    )}
                    <button
                      className={`hl-btn-outline hl-btn-flex${!att?.attachmentId ? " hl-btn-muted" : ""}`}
                      onClick={() => att?.attachmentId && handleOriginalClick(report)}
                      disabled={!att?.attachmentId}
                    >
                      👁 {t("healthLocker.original")}
                    </button>
                  </div>

                  {/* Kebab menu */}
                  <div style={{ position: "absolute", top: "12px", right: "10px" }} ref={openMenuIndex === idx ? menuRef : null}>
                    <button className="hl-kebab" onClick={() => setOpenMenuIndex(openMenuIndex === idx ? null : idx)} aria-label="more options">⋮</button>
                    {openMenuIndex === idx && (
                      <>
                        <div className="hl-menu-overlay" onClick={() => setOpenMenuIndex(null)} />
                        <div className="hl-menu">
                          <button className="hl-menu-item danger" onClick={() => deleteRecord(report.healthRecordId)}>
                            🗑 {t("healthLocker.delete")}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* FAB */}
      <button className="hl-fab" onClick={openAddRecord} aria-label={t("healthLocker.addRecord")}>+</button>
    </div>
  );
};

export default HealthLocker;
